import React from "react";
import { useNavigate } from "react-router-dom";

const style = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(180deg, #EEF4FA 0%, #F8FAFC 45%, #FFFFFF 100%)",
    fontFamily: "'Manrope', sans-serif",
  },

  navWrapper: {
    maxWidth: "1120px",
    margin: "0 auto",
    padding: "32px 32px 0",
  },

  backButton: {
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    color: "#2563EB",
    borderRadius: "999px",
    padding: "8px 14px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },

  contentWrapper: {
    maxWidth: "1120px",
    margin: "0 auto",
    padding: "28px 32px 64px",
  },

  documentCard: {
    background: "#FFFFFF",
    border: "1px solid #D8E1EC",
    borderRadius: "18px",
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
    padding: "52px 60px",
  },

  headerBlock: {
    borderBottom: "1px solid #E2E8F0",
    paddingBottom: "28px",
    marginBottom: "32px",
  },

  label: {
    display: "inline-block",
    background: "#DBEAFE",
    color: "#1D4ED8",
    border: "1px solid #BFDBFE",
    borderRadius: "999px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: "18px",
  },

  title: {
    fontSize: "38px",
    color: "#0B1F3A",
    fontWeight: 800,
    letterSpacing: "-0.04em",
    margin: 0,
  },

  subtitle: {
    fontSize: "14px",
    color: "#64748B",
    marginTop: "12px",
    marginBottom: 0,
    fontWeight: 500,
  },

  section: {
    marginTop: "34px",
    padding: "24px 26px",
    background: "#F8FAFC",
    border: "1px solid #E2E8F0",
    borderRadius: "14px",
  },

  sectionTitle: {
    fontSize: "20px",
    color: "#0B1F3A",
    fontWeight: 750,
    marginTop: 0,
    marginBottom: "18px",
    paddingBottom: "12px",
    borderBottom: "1px solid #DDE7F1",
  },

  bodyText: {
    fontSize: "14.5px",
    color: "#334155",
    lineHeight: 1.75,
    marginTop: "1rem",
    marginBottom: 0,
  },

  list: {
    display: "block",
    marginTop: "1rem",
    marginBottom: "1rem",
    paddingLeft: "2rem",
    color: "#334155",
    fontSize: "14.5px",
    lineHeight: 1.8,
    listStyleType: "disc",
    listStylePosition: "outside",
  },

  listItem: {
    display: "list-item",
    marginBottom: "0.75rem",
    paddingLeft: "0.25rem",
  },

  strong: {
    color: "#1E293B",
    fontWeight: 750,
  },

  link: {
    color: "#2563EB",
    fontWeight: 700,
    textDecoration: "none",
  },
};

// Served at /security for the footer link; the machine-readable version lives at
// /.well-known/security.txt (RFC 9116).
const SecurityPage = () => {
  const navigate = useNavigate();

  return (
    <div style={style.page}>
      <div style={style.navWrapper}>
        {/* Go back rather than a fixed route — the footer links here from several pages. */}
        <button onClick={() => navigate(-1)} style={style.backButton}>
          ← Back
        </button>
      </div>

      <main style={style.contentWrapper}>
        <article style={style.documentCard}>
          <header style={style.headerBlock}>
            <span style={style.label}>Security Policy</span>

            <h1 style={style.title}>DEEBug Security Policy</h1>

            <p style={style.subtitle}>Last updated June 17, 2026</p>
          </header>

          <section style={style.section}>
            <h2 style={style.sectionTitle}>Introduction</h2>

            <p style={style.bodyText}>
              This vulnerability disclosure policy applies to any vulnerabilities
              you are considering reporting to us, DEEBug. We recommend reading
              this vulnerability disclosure policy fully before you report a
              vulnerability and always acting in compliance with it. We value
              those who take the time and effort to report security
              vulnerabilities according to this policy. However, we do not offer
              monetary rewards for vulnerability disclosures.
            </p>
          </section>

          <section style={style.section}>
            <h2 style={style.sectionTitle}>Security.txt File</h2>

            <p style={style.bodyText}>
              As per the IETF standard RFC 9116, updates and contact information
              regarding this policy can be found at:{" "}
              <a href="/.well-known/security.txt" style={style.link}>
                deebug.ca/.well-known/security.txt
              </a>
            </p>
          </section>

          <section style={style.section}>
            <h2 style={style.sectionTitle}>Reporting</h2>

            <p style={style.bodyText}>
              If you believe you have found a security vulnerability, please
              submit your report to us using the following email:{" "}
              <a href="mailto:sigma.jahan@dal.ca" style={style.link}>
                sigma.jahan@dal.ca
              </a>
              . In your report, please include details of:
            </p>

            <ul style={style.list}>
              <li style={style.listItem}>
                The website, IP, or page where the vulnerability can be observed.
              </li>
              <li style={style.listItem}>
                A brief description of the type of vulnerability, for example,
                “XSS vulnerability”.
              </li>
              <li style={style.listItem}>
                Steps to reproduce. These should be a benign, non-destructive
                proof of concept. This helps ensure that the report can be
                triaged quickly and accurately.
              </li>
            </ul>
          </section>

          <section style={style.section}>
            <h2 style={style.sectionTitle}>What to Expect</h2>

            <p style={style.bodyText}>
              After you have submitted your report, we will respond to your
              report within 5 working days and aim to triage your report within
              10 working days. We will also aim to keep you informed of our
              progress. Priority for remediation is assessed by looking at the
              impact, severity, and exploit complexity.
            </p>

            <p style={style.bodyText}>
              Vulnerability reports may take some time to triage or address.
              You are welcome to enquire on the status but should avoid doing so
              more than once every 14 days. This allows our team to focus on
              remediation.
            </p>

            <p style={style.bodyText}>
              We will notify you when the reported vulnerability is remediated,
              and you may be invited to confirm that the solution covers the
              vulnerability adequately. Once your vulnerability has been
              resolved, we welcome requests to disclose your report, provided
              public release remains coordinated with us.
            </p>
          </section>

          <section style={style.section}>
            <h2 style={style.sectionTitle}>Guidance</h2>

            <p style={style.bodyText}>
              <strong style={style.strong}>You must not:</strong>
            </p>

            <ul style={style.list}>
              <li style={style.listItem}>Break any applicable law or regulation.</li>
              <li style={style.listItem}>
                Access unnecessary, excessive, or significant amounts of data.
              </li>
              <li style={style.listItem}>
                Modify data in DEEBug systems or services.
              </li>
              <li style={style.listItem}>
                Use high-intensity, invasive, or destructive scanning tools to
                find vulnerabilities.
              </li>
              <li style={style.listItem}>
                Attempt or report any form of denial of service, including
                overwhelming a service with a high volume of requests.
              </li>
              <li style={style.listItem}>
                Disrupt DEEBug services or systems.
              </li>
              <li style={style.listItem}>
                Submit reports detailing non-exploitable vulnerabilities or
                reports indicating that services do not fully align with best
                practice, such as missing security headers.
              </li>
              <li style={style.listItem}>
                Submit reports detailing TLS configuration weaknesses, such as
                weak cipher suite support or the presence of TLS 1.0 support.
              </li>
              <li style={style.listItem}>
                Communicate any vulnerabilities or associated details other than
                by the means described in the published security.txt file.
              </li>
              <li style={style.listItem}>
                Social engineer, phish, or physically attack DEEBug staff or
                infrastructure.
              </li>
              <li style={style.listItem}>
                Demand financial compensation in order to disclose any
                vulnerabilities.
              </li>
            </ul>

            <p style={style.bodyText}>
              <strong style={style.strong}>You must:</strong>
            </p>

            <ul style={style.list}>
              <li style={style.listItem}>
                Always comply with data protection rules and not violate the
                privacy of DEEBug users, staff, contractors, services, or
                systems.
              </li>
              <li style={style.listItem}>
                Not share, redistribute, or fail to properly secure data
                retrieved from DEEBug systems or services.
              </li>
              <li style={style.listItem}>
                Securely delete all data retrieved during your research as soon
                as it is no longer required or within 1 month of the
                vulnerability being resolved, whichever occurs first.
              </li>
            </ul>
          </section>

          <section style={style.section}>
            <h2 style={style.sectionTitle}>Legalities</h2>

            <p style={style.bodyText}>
              This policy is designed to be compatible with common vulnerability
              disclosure good practice. It does not give you permission to act
              in any manner that is inconsistent with the law, or which might
              cause DEEBug or partner organizations to be in breach of any legal
              obligations.
            </p>
          </section>
        </article>
      </main>
    </div>
  );
};

export default SecurityPage;